import { emotionTheme } from '@styles/theme'
import { buildAlignedTotalsRow, getColumnIndex } from '@components/shared/Table/helpers/buildAlignedTotalsRow'
import { numberMask } from '@utils/masks/cpfMask'

import type { AlignedTotalsValuesProps } from '@components/shared/Table/helpers/buildAlignedTotalsRow'
import type { ColumnDefProps } from '@components/shared/Table/types'
import type { WeeklyBreakdownResponseProps } from '../context/types'

const DAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const DAILY_ROW_BACKGROUND = emotionTheme.colors.gray[100]
const DAILY_ROW_FONT_WEIGHT = 600
const TOTALS_ROW_BACKGROUND = emotionTheme.colors.gray.footer

interface ParseWeeklyBreakdownClientTotalProps {
  /** Colunas do corpo da tabela (`parseWeeklyBreakdownClientGrid(...)[0]`). */
  columns: ColumnDefProps[]
  data: WeeklyBreakdownResponseProps | null
  weekSelected: number
}

const hasValue = (value: unknown) => value !== null && value !== undefined && value !== ''

/** Mapeia os totais diários da semana selecionada para os ids das colunas de dia do corpo. */
const parseDayValues = (
  weekData: { valorTotal: number }[],
  weekSelected: number,
  currency: boolean
): AlignedTotalsValuesProps => {
  const startIndex = (weekSelected - 1) * 7

  return weekData.slice(startIndex, startIndex + 7).reduce<AlignedTotalsValuesProps>((values, dayData, index) => {
    const columnId = `${DAY_NAMES[index]}${weekSelected}`
    const dayValue = dayData?.valorTotal

    values[columnId] = currency
      ? hasValue(dayValue)
        ? { value: dayValue, currency: true }
        : { value: '-' }
      : { value: numberMask(String(hasValue(dayValue) ? dayValue : 0)) }

    return values
  }, {})
}

/**
 * Linhas de total do Detalhamento Semanal do Cliente, espelhadas nas colunas do corpo para que
 * cada valor caia exatamente sob a sua coluna:
 * - 3 linhas de totais por dia (quantidade, faturamento bruto, faturamento líquido);
 * - 1 linha de total da semana, com Total Bruto sob "Total Bruto (R$)" e Total Líquido sob
 *   "Total Líquido (R$)" — na ordem bruto → líquido.
 */
export const parseWeeklyBreakdownClientTotalGrid = ({
  columns,
  data,
  weekSelected
}: ParseWeeklyBreakdownClientTotalProps): ColumnDefProps[][] => {
  if (!columns.length || !data) return []

  const { dailyGrossTotals = [], dailyNetTotals = [], dailyTotalQuantities = [] } = data

  // Rótulo das linhas diárias ocupa todas as colunas base (até Total Líquido), alinhado à
  // esquerda como o "Total Registros" — os valores por dia ficam sob as colunas de dia.
  const dailyLabelSpan = getColumnIndex(columns, 'netTotal') + 1

  const buildDailyRow = (label: string, weekData: { valorTotal: number }[], currency: boolean) =>
    buildAlignedTotalsRow({
      columns,
      label,
      labelSpan: dailyLabelSpan,
      background: DAILY_ROW_BACKGROUND,
      fontWeight: DAILY_ROW_FONT_WEIGHT,
      values: parseDayValues(weekData, weekSelected, currency)
    })

  const parsedData: ColumnDefProps[][] = []

  if (dailyTotalQuantities.length > 0) {
    parsedData.push(buildDailyRow('Qtd. total/dia:', dailyTotalQuantities, false))
  }

  if (dailyGrossTotals.length > 0) {
    parsedData.push(buildDailyRow('Fat. bruto/dia:', dailyGrossTotals, true))
  }

  if (dailyNetTotals.length > 0) {
    parsedData.push(buildDailyRow('Fat. líquido/dia:', dailyNetTotals, true))
  }

  const weekTotalsRow = buildAlignedTotalsRow({
    columns,
    label: `Total Registros: ${numberMask(String(data.totalItems || 0))}`,
    labelSpan: getColumnIndex(columns, 'grossTotal'),
    background: TOTALS_ROW_BACKGROUND,
    values: {
      grossTotal: { label: 'Total Bruto', value: data.grossTotalAmount || 0, currency: true },
      netTotal: { label: 'Total Líquido', value: data.netTotalAmount || 0, currency: true }
    }
  })

  if (weekTotalsRow.length) parsedData.push(weekTotalsRow)

  return parsedData.filter((row) => row.length > 0)
}
